import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Archive,
  BarChart3,
  Database,
  Download,
  FileJson,
  Image,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
  Upload,
  Users
} from 'lucide-react';
import { BACKEND_URL } from '../../firebase/config';

const MONTH_INDEX = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11
};

const DATA_COLLECTIONS = [
  ['students', 'Students'],
  ['users', 'Faculty/HOD profiles'],
  ['facultyAssignments', 'Faculty assignments'],
  ['studentOffices', 'Student offices'],
  ['semesterSubjects', 'Semester subjects'],
  ['attendance', 'Attendance reports'],
  ['courseAttendance', 'Course attendance'],
  ['courseMarks', 'Course marks'],
  ['iaTest1', 'IAT-1 summaries'],
  ['iaTest2', 'IAT-2 summaries'],
  ['parentMeetRecords', 'Parent meet records'],
  ['achievements', 'Achievements'],
  ['leaves', 'Leave records'],
  ['notifications', 'Notifications'],
  ['messages', 'Messages']
];

const getSessionToken = () => {
  const sessionKeys = ['adminSession', 'userSession', 'studentSession'];

  for (const key of sessionKeys) {
    const rawSession = window.sessionStorage.getItem(key);
    if (!rawSession) continue;

    try {
      const token = JSON.parse(rawSession)?.sessionToken || '';
      if (token) return token;
    } catch (error) {
      console.warn(`Could not parse ${key}.`, error);
    }
  }

  return '';
};

const apiUrl = (path) => `${BACKEND_URL.replace(/\/$/, '')}${path}`;
const numberOrZero = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};
const formatPercent = (value) => `${numberOrZero(value).toFixed(2)}%`;
const safeFilePart = (value) => String(value || 'archive').replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '') || 'archive';

const asArray = (collection) => {
  if (Array.isArray(collection)) return collection;
  if (!collection || typeof collection !== 'object') return [];

  return Object.entries(collection).map(([id, value]) => (
    value && typeof value === 'object' && !Array.isArray(value)
      ? { id, ...value }
      : { id, value }
  ));
};

const dateFromValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && Number.isFinite(value.seconds)) return new Date(value.seconds * 1000);

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const academicYearFromDate = (date) => {
  const year = date.getFullYear();
  return date.getMonth() >= 7 ? `${year}-${String(year + 1).slice(-2)}` : `${year - 1}-${String(year).slice(-2)}`;
};

const defaultAcademicYear = () => academicYearFromDate(new Date());

const getRecordAcademicYear = (record = {}) => {
  const explicitYear = record.academicYear || record.academicYearLabel || record.yearLabel || record.session;
  if (explicitYear) return String(explicitYear);

  if (record.year && record.month) {
    const monthIndex = MONTH_INDEX[String(record.month).trim().toLowerCase()];
    if (monthIndex !== undefined) {
      const year = Number(record.year);
      if (Number.isFinite(year)) {
        return monthIndex >= 7 ? `${year}-${String(year + 1).slice(-2)}` : `${year - 1}-${String(year).slice(-2)}`;
      }
    }
  }

  const date = dateFromValue(
    record.createdAt ||
    record.updatedAt ||
    record.submittedAt ||
    record.approvedAt ||
    record.meetingDate ||
    record.date
  );

  return date ? academicYearFromDate(date) : '';
};

const belongsToAcademicYear = (record, academicYear) => {
  const recordYear = getRecordAcademicYear(record);
  return !recordYear || recordYear === academicYear;
};

const studentName = (student = {}) => (
  student.name ||
  [student.firstName, student.fatherName, student.surname].filter(Boolean).join(' ') ||
  student.fullName ||
  student.studentName ||
  ''
).trim();

const getStudentRoll = (student = {}) => String(student.rollNumber || student.id || '').trim().toUpperCase();

const getAttendanceNumbers = (record = {}) => {
  const theoryConducted = numberOrZero(record.theoryTotal?.conducted ?? record.theoryConducted ?? record.lectureConducted);
  const theoryAttended = numberOrZero(record.theoryTotal?.attended ?? record.theoryAttended ?? record.lectureAttended);
  const practicalConducted =
    numberOrZero(record.practicalTotal?.conducted ?? record.practicalConducted) +
    numberOrZero(record.tutorialTotal?.conducted ?? record.tutorialConducted);
  const practicalAttended =
    numberOrZero(record.practicalTotal?.attended ?? record.practicalAttended) +
    numberOrZero(record.tutorialTotal?.attended ?? record.tutorialAttended);

  const totalConducted = numberOrZero(record.totalLectures) || theoryConducted + practicalConducted;
  const totalAttended = numberOrZero(record.attendedLectures) || theoryAttended + practicalAttended;
  const percentage = Number.isFinite(Number(record.overallPercentage)) && record.overallPercentage !== ''
    ? Number(record.overallPercentage)
    : totalConducted > 0
      ? (totalAttended / totalConducted) * 100
      : 0;

  return {
    theoryConducted,
    theoryAttended,
    practicalConducted,
    practicalAttended,
    totalConducted,
    totalAttended,
    percentage
  };
};

const getExamValue = (record, examKey) => {
  const value = record?.[examKey]?.marks ?? record?.[`${examKey}Marks`];
  return value === '' || value === undefined || value === null ? null : numberOrZero(value);
};

const getExamMax = (record, examKey) => numberOrZero(record?.[`${examKey}Max`] || record?.iatMax);

const addAverage = (target, marks, max) => {
  if (marks === null || !max) return;
  target.marks += marks;
  target.max += max;
  target.count += 1;
  if ((marks / max) * 100 < 40) target.failed += 1;
};

const buildPhotoManifest = (data = {}) => {
  const photos = [];
  const addPhoto = (ownerType, ownerId, ownerName, record = {}) => {
    const photoPath = record.photoPath || '';
    const photoUrl = record.photoUrl || record.url || '';
    if (!photoPath && !photoUrl) return;

    photos.push({
      ownerType,
      ownerId,
      ownerName,
      photoPath,
      photoUrl,
      driveFileReference: photoPath || photoUrl
    });
  };

  asArray(data.students).forEach((student) => {
    addPhoto('student', getStudentRoll(student), studentName(student), student);
  });

  asArray(data.users).forEach((user) => {
    const role = String(user.role || user.userType || '').toLowerCase();
    addPhoto(role.includes('hod') || role === 'administrator' || role === 'admin' ? 'hod' : 'faculty', user.loginId || user.username || user.email || user.id, user.name || user.email, user);
  });

  asArray(data.accounts?.faculty).forEach((account) => {
    addPhoto('faculty-account', account.loginId || account.email || account.id, account.name || account.email, account);
  });

  asArray(data.accounts?.hod).forEach((account) => {
    addPhoto('hod-account', account.loginId || account.email || account.id, account.name || account.email, account);
  });

  const unique = new Map();
  photos.forEach((photo) => {
    const key = `${photo.ownerType}:${photo.ownerId}:${photo.driveFileReference}`;
    unique.set(key, photo);
  });

  return [...unique.values()].sort((a, b) => `${a.ownerType}${a.ownerId}`.localeCompare(`${b.ownerType}${b.ownerId}`));
};

const buildAnnualAnalysis = (data = {}, academicYear = defaultAcademicYear()) => {
  const students = asArray(data.students);
  const users = asArray(data.users);
  const facultyAccounts = asArray(data.accounts?.faculty);
  const attendanceRecords = asArray(data.attendance).filter((record) => belongsToAcademicYear(record, academicYear));
  const courseAttendanceRecords = asArray(data.courseAttendance).filter((record) => belongsToAcademicYear(record, academicYear));
  const courseMarks = asArray(data.courseMarks).filter((record) => belongsToAcademicYear(record, academicYear));
  const parentMeetRecords = asArray(data.parentMeetRecords).filter((record) => belongsToAcademicYear(record, academicYear));
  const achievements = asArray(data.achievements).filter((record) => belongsToAcademicYear(record, academicYear));
  const leaves = asArray(data.leaves).filter((record) => belongsToAcademicYear(record, academicYear));

  const studentMap = new Map(students.map((student) => [getStudentRoll(student), student]));
  const attendancePercentages = attendanceRecords
    .map(getAttendanceNumbers)
    .filter((numbers) => numbers.totalConducted > 0);
  const averageAttendance = attendancePercentages.length
    ? attendancePercentages.reduce((sum, item) => sum + item.percentage, 0) / attendancePercentages.length
    : 0;
  const defaulters = attendancePercentages.filter((item) => item.percentage < 75).length;

  const facultyIds = new Set();
  facultyAccounts.forEach((account) => facultyIds.add(account.loginId || account.email || account.id));
  users.forEach((user) => {
    const role = String(user.role || user.userType || '').toLowerCase();
    if (!['hod', 'administrator', 'admin', 'student'].includes(role)) {
      facultyIds.add(user.loginId || user.username || user.email || user.id);
    }
  });

  const byDivision = new Map();
  attendanceRecords.forEach((record) => {
    const roll = String(record.rollNumber || record.id || '').toUpperCase();
    const student = studentMap.get(roll) || {};
    const division = record.division || student.division || 'Unassigned';
    const numbers = getAttendanceNumbers(record);
    if (!byDivision.has(division)) {
      byDivision.set(division, { division, students: new Set(), totalPercent: 0, percentCount: 0, defaulters: 0 });
    }
    const bucket = byDivision.get(division);
    bucket.students.add(roll || record.id);
    if (numbers.totalConducted > 0) {
      bucket.totalPercent += numbers.percentage;
      bucket.percentCount += 1;
      if (numbers.percentage < 75) bucket.defaulters += 1;
    }
  });

  const subjectMap = new Map();
  courseMarks.forEach((record) => {
    const key = `${record.subjectCode || record.subjectId || 'Subject'} - ${record.subjectName || 'Course'}`;
    if (!subjectMap.has(key)) {
      subjectMap.set(key, {
        subject: key,
        division: record.division || record.teachingDivision || '-',
        iat1: { marks: 0, max: 0, count: 0, failed: 0 },
        iat2: { marks: 0, max: 0, count: 0, failed: 0 },
        ese: { marks: 0, max: 0, count: 0, failed: 0 }
      });
    }
    const subject = subjectMap.get(key);
    addAverage(subject.iat1, getExamValue(record, 'iat1'), getExamMax(record, 'iat1'));
    addAverage(subject.iat2, getExamValue(record, 'iat2'), getExamMax(record, 'iat2'));
    addAverage(subject.ese, getExamValue(record, 'ese'), getExamMax(record, 'ese'));
  });

  const photoManifest = buildPhotoManifest(data);

  return {
    academicYear,
    generatedAt: new Date().toISOString(),
    summary: {
      students: students.length,
      faculty: [...facultyIds].filter(Boolean).length,
      averageAttendance,
      defaulters,
      courseMarks: courseMarks.length,
      parentMeetRecords: parentMeetRecords.length,
      achievements: achievements.length,
      leaves: leaves.length,
      photos: photoManifest.length
    },
    collectionCounts: DATA_COLLECTIONS.map(([key, label]) => ({
      key,
      label,
      count: asArray(data[key]).length
    })),
    byDivision: [...byDivision.values()].map((item) => ({
      division: item.division,
      students: item.students.size,
      averageAttendance: item.percentCount ? item.totalPercent / item.percentCount : 0,
      defaulters: item.defaulters
    })).sort((a, b) => a.division.localeCompare(b.division, undefined, { numeric: true })),
    bySubject: [...subjectMap.values()].map((subject) => ({
      subject: subject.subject,
      division: subject.division,
      iat1Average: subject.iat1.count ? (subject.iat1.marks / subject.iat1.max) * 100 : 0,
      iat2Average: subject.iat2.count ? (subject.iat2.marks / subject.iat2.max) * 100 : 0,
      eseAverage: subject.ese.count ? (subject.ese.marks / subject.ese.max) * 100 : 0,
      entries: Math.max(subject.iat1.count, subject.iat2.count, subject.ese.count),
      failedEntries: subject.iat1.failed + subject.iat2.failed + subject.ese.failed
    })).sort((a, b) => a.subject.localeCompare(b.subject)),
    evidenceCounts: [
      { label: 'Attendance records', count: attendanceRecords.length },
      { label: 'Course attendance records', count: courseAttendanceRecords.length },
      { label: 'Course marks records', count: courseMarks.length },
      { label: 'Parent meet records', count: parentMeetRecords.length },
      { label: 'Achievements', count: achievements.length },
      { label: 'Leave records', count: leaves.length }
    ],
    photoManifest
  };
};

const buildResetDatabase = (data = {}, academicYear) => {
  const resetAt = new Date().toISOString();
  const hodUsers = {};

  Object.entries(data.users || {}).forEach(([id, user]) => {
    const role = String(user?.role || user?.userType || '').toLowerCase();
    if (['hod', 'administrator', 'admin'].includes(role)) {
      hodUsers[id] = {
        ...user,
        updatedAt: resetAt
      };
    }
  });

  return {
    accounts: {
      hod: data.accounts?.hod || {},
      faculty: {},
      students: {}
    },
    users: hodUsers,
    dashboardConfigs: data.dashboardConfigs || {},
    systemSettings: {
      ...(data.systemSettings || {}),
      lastArchivedAcademicYear: academicYear,
      lastResetAt: resetAt,
      photoArchiveCutoff: resetAt,
      photoArchiveNote: 'Old Google Drive photo folders are retained as archive evidence. New batch latest-photo lookups ignore files before this cutoff.'
    }
  };
};

const downloadJson = (fileName, payload) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const StatCard = ({ icon: Icon, label, value, tone = 'indigo' }) => {
  const tones = {
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    rose: 'bg-rose-50 text-rose-700 border-rose-100'
  };

  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.indigo}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5" />
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
        </div>
      </div>
    </div>
  );
};

const SystemArchiveWidget = () => {
  const restoreInputRef = useRef(null);
  const [database, setDatabase] = useState(null);
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear());
  const [confirmReset, setConfirmReset] = useState('');
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const annualAnalysis = useMemo(() => buildAnnualAnalysis(database || {}, academicYear), [database, academicYear]);

  const fetchDatabase = async () => {
    const token = getSessionToken();
    if (!token) {
      setError('HOD session expired. Please log out and sign in again.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(apiUrl('/api/db'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.error || 'Could not load Google Drive database.');
      setDatabase(result.data || {});
      setMessage('Google Drive database loaded.');
    } catch (loadError) {
      console.error('Archive database load failed:', loadError);
      setError(loadError.message || 'Could not load Google Drive database.');
    } finally {
      setLoading(false);
    }
  };

  const writeDatabase = async (nextDatabase) => {
    const token = getSessionToken();
    if (!token) throw new Error('HOD session expired. Please log out and sign in again.');

    const response = await fetch(apiUrl('/api/db'), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ data: nextDatabase })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.success) throw new Error(result.error || 'Could not update Google Drive database.');
    return result;
  };

  useEffect(() => {
    fetchDatabase();
  }, []);

  const buildBackupPackage = () => ({
    app: 'YBIT Mentoring System',
    schemaVersion: 1,
    academicYear,
    exportedAt: new Date().toISOString(),
    photoStrategy: 'The backup stores photo references and a manifest. Google Drive photo files remain in Drive; keep or download the Drive photo folders for full evidence archive.',
    annualAnalysis,
    photoManifest: annualAnalysis.photoManifest,
    data: database || {}
  });

  const downloadBackup = (prefix = 'ybit-backup') => {
    if (!database) {
      setError('Load the database before downloading a backup.');
      return;
    }

    const fileName = `${prefix}-${safeFilePart(academicYear)}-${new Date().toISOString().slice(0, 10)}.json`;
    downloadJson(fileName, buildBackupPackage());
    setMessage('Backup downloaded. Keep this file safely before reset.');
  };

  const downloadAnalysis = () => {
    downloadJson(`ybit-annual-analysis-${safeFilePart(academicYear)}.json`, annualAnalysis);
    setMessage('Annual analysis downloaded.');
  };

  const downloadPhotoManifest = () => {
    downloadJson(`ybit-photo-manifest-${safeFilePart(academicYear)}.json`, {
      academicYear,
      exportedAt: new Date().toISOString(),
      photos: annualAnalysis.photoManifest
    });
    setMessage('Photo manifest downloaded.');
  };

  const restoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setWorking(true);
    setError('');
    setMessage('');

    try {
      const parsed = JSON.parse(await file.text());
      const restoredData = parsed.data || parsed.database || parsed;
      if (!restoredData || typeof restoredData !== 'object' || Array.isArray(restoredData)) {
        throw new Error('Backup file does not contain a valid database.');
      }

      await writeDatabase(restoredData);
      setDatabase(restoredData);
      setMessage('Backup restored to Google Drive. Refresh other open dashboards before continuing.');
    } catch (restoreError) {
      console.error('Backup restore failed:', restoreError);
      setError(restoreError.message || 'Could not restore backup.');
    } finally {
      setWorking(false);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  };

  const resetToDefaults = async () => {
    if (!database) {
      setError('Load the database before resetting.');
      return;
    }
    if (confirmReset !== 'RESET') {
      setError('Type RESET exactly before clearing the site.');
      return;
    }
    if (!window.confirm('This will clear students, faculty, marks, attendance, reports, and assignments from the active site. A backup will be downloaded first. Continue?')) {
      return;
    }

    setWorking(true);
    setError('');
    setMessage('');

    try {
      downloadBackup('ybit-pre-reset-backup');
      const cleanDatabase = buildResetDatabase(database, academicYear);
      await writeDatabase(cleanDatabase);
      setDatabase(cleanDatabase);
      setConfirmReset('');
      setMessage('Site reset to defaults. HOD login is preserved; faculty, students, reports, and assignments are cleared.');
    } catch (resetError) {
      console.error('Reset failed:', resetError);
      setError(resetError.message || 'Could not reset the site.');
    } finally {
      setWorking(false);
    }
  };

  if (loading && !database) {
    return (
      <div className="flex items-center justify-center py-10 text-gray-500">
        <RefreshCw className="mr-3 h-5 w-5 animate-spin" />
        Loading Google Drive archive data
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Users} label="Students" value={annualAnalysis.summary.students} />
        <StatCard icon={Database} label="Faculty" value={annualAnalysis.summary.faculty} tone="emerald" />
        <StatCard icon={BarChart3} label="Avg Attendance" value={formatPercent(annualAnalysis.summary.averageAttendance)} tone="amber" />
        <StatCard icon={ShieldAlert} label="Defaulters" value={annualAnalysis.summary.defaulters} tone="rose" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-4 flex items-center gap-2">
            <Archive className="h-5 w-5 text-indigo-700" />
            <h3 className="text-lg font-bold text-gray-900">Backup And Restore</h3>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase text-gray-500">Academic Year</span>
              <input
                value={academicYear}
                onChange={(event) => setAcademicYear(event.target.value)}
                placeholder="2025-26"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </label>

            <div className="flex flex-wrap items-end gap-2">
              <button
                type="button"
                onClick={fetchDatabase}
                disabled={loading || working}
                className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => downloadBackup()}
                disabled={!database || working}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:bg-gray-400"
              >
                <Download className="mr-2 h-4 w-4" />
                Full Backup
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={downloadAnalysis}
              disabled={!database || working}
              className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
            >
              <FileJson className="mr-2 h-4 w-4" />
              Annual Analysis
            </button>
            <button
              type="button"
              onClick={downloadPhotoManifest}
              disabled={!database || working}
              className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
            >
              <Image className="mr-2 h-4 w-4" />
              Photo Manifest
            </button>
            <label className="inline-flex cursor-pointer items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <Upload className="mr-2 h-4 w-4" />
              Restore Backup
              <input
                ref={restoreInputRef}
                type="file"
                accept="application/json,.json"
                onChange={restoreBackup}
                className="hidden"
                disabled={working}
              />
            </label>
          </div>
        </div>

        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="mb-3 flex items-center gap-2 text-red-800">
            <ShieldAlert className="h-5 w-5" />
            <h3 className="text-lg font-bold">Reset For New Batch</h3>
          </div>
          <p className="text-sm text-red-700">
            This clears active students, faculty, attendance, marks, parent meet records, achievements, messages, and assignments. HOD login stays active. Old Drive photos are kept as archive evidence and ignored for new latest-photo lookups.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <input
              value={confirmReset}
              onChange={(event) => setConfirmReset(event.target.value)}
              placeholder="Type RESET"
              className="flex-1 rounded-lg border border-red-300 px-3 py-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
            <button
              type="button"
              onClick={resetToDefaults}
              disabled={working || confirmReset !== 'RESET'}
              className="inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:bg-gray-400"
            >
              {working ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Reset Site
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {message && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{message}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-4 py-3">
          <h3 className="text-lg font-bold text-gray-900">NBA/NAAC Evidence Snapshot</h3>
          <p className="text-sm text-gray-500">Counts and summaries for {academicYear || 'selected academic year'}.</p>
        </div>
        <div className="grid gap-4 p-4 lg:grid-cols-2">
          <div className="overflow-x-auto">
            <h4 className="mb-2 text-sm font-bold uppercase text-gray-600">Attendance By Division</h4>
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2">Division</th>
                  <th className="border-b border-gray-200 px-3 py-2">Students</th>
                  <th className="border-b border-gray-200 px-3 py-2">Average</th>
                  <th className="border-b border-gray-200 px-3 py-2">Defaulters</th>
                </tr>
              </thead>
              <tbody>
                {annualAnalysis.byDivision.slice(0, 8).map((row) => (
                  <tr key={row.division}>
                    <td className="border-b border-gray-100 px-3 py-2 font-semibold">{row.division}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{row.students}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{formatPercent(row.averageAttendance)}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{row.defaulters}</td>
                  </tr>
                ))}
                {!annualAnalysis.byDivision.length && (
                  <tr><td colSpan="4" className="px-3 py-6 text-center text-gray-500">No attendance records yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="overflow-x-auto">
            <h4 className="mb-2 text-sm font-bold uppercase text-gray-600">Academic By Subject</h4>
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="border-b border-gray-200 px-3 py-2">Subject</th>
                  <th className="border-b border-gray-200 px-3 py-2">IAT-1</th>
                  <th className="border-b border-gray-200 px-3 py-2">IAT-2</th>
                  <th className="border-b border-gray-200 px-3 py-2">ESE</th>
                  <th className="border-b border-gray-200 px-3 py-2">Fail Entries</th>
                </tr>
              </thead>
              <tbody>
                {annualAnalysis.bySubject.slice(0, 8).map((row) => (
                  <tr key={`${row.subject}-${row.division}`}>
                    <td className="border-b border-gray-100 px-3 py-2 font-semibold">{row.subject}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{formatPercent(row.iat1Average)}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{formatPercent(row.iat2Average)}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{formatPercent(row.eseAverage)}</td>
                    <td className="border-b border-gray-100 px-3 py-2">{row.failedEntries}</td>
                  </tr>
                ))}
                {!annualAnalysis.bySubject.length && (
                  <tr><td colSpan="5" className="px-3 py-6 text-center text-gray-500">No course marks yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="mb-3 text-sm font-bold uppercase text-gray-600">Evidence Counts</h4>
          <div className="grid gap-2 sm:grid-cols-2">
            {annualAnalysis.evidenceCounts.map((item) => (
              <div key={item.label} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm">
                <span className="text-gray-600">{item.label}</span>
                <span className="font-bold text-gray-900">{item.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
          <div className="mb-2 flex items-center gap-2 font-bold">
            <Image className="h-5 w-5" />
            Photo Archive Strategy
          </div>
          <p>
            The full backup includes {annualAnalysis.photoManifest.length} photo references. The reset does not delete Google Drive photos; it marks a cutoff so old batch photos remain archived but are not auto-picked for new students with the same roll numbers.
          </p>
          <p className="mt-2">
            For a full offline evidence archive, download the Full Backup and Photo Manifest, then download or duplicate the Google Drive photo folders for the same academic year.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SystemArchiveWidget;
